import { useSearch } from "@tanstack/react-router";
import type { ColumnDef } from "@tanstack/react-table";

import { DataTable } from "@/components/data";

import { Chip, FlagChip } from "../../chips";
import { OptionLink } from "../../links";
import { MiniBar } from "../bars";
import type { PageProps } from "../index";
import { Footnote, MUTED_ROW, PageHead, RefLegend } from "../kit";
import { pageExtra } from "../params";
import { RowsPage, TruncatedNote, type RowsData } from "../rows";

export interface FeaturePerfRow {
  option_id: string;
  option_name: string;
  item_id: string;
  item_name: string;
  phase: string | null;
  dropped: boolean;
  overall_rating: number | null;
  option_low_evidence: boolean | null;
  feature: string;
  rating_avg: number;
  rating_min: number;
  min_criterion: string | null;
  criteria_count: number;
}

export type PerfMode = "average" | "minimum";

export interface PerfOption {
  option_id: string;
  option_name: string;
  item_id: string;
  dropped: boolean;
  lowEvidence: boolean;
  byFeature: Record<string, { average: number; minimum: number }>;
}

/**
 * One line per option, one column per feature. Rows arrive best overall rating
 * first, an option's rows together. When the row cap cut the list, the last
 * option may be missing features, so it is left out.
 */
export function pivotFeatures(rows: FeaturePerfRow[], truncated: boolean): { features: string[]; options: PerfOption[] } {
  const options: PerfOption[] = [];
  const byId = new Map<string, PerfOption>();
  for (const row of rows) {
    let option = byId.get(row.option_id);
    if (!option) {
      option = {
        option_id: row.option_id,
        option_name: row.option_name,
        item_id: row.item_id,
        dropped: row.dropped,
        lowEvidence: row.option_low_evidence === true,
        byFeature: {},
      };
      byId.set(row.option_id, option);
      options.push(option);
    }
    option.byFeature[row.feature] = { average: row.rating_avg, minimum: row.rating_min };
  }
  // The cap may have cut the last option's rows; a lone option is kept, cut or not.
  if (truncated && options.length > 1) options.pop();
  const features = new Set(options.flatMap((option) => Object.keys(option.byFeature)));
  return { features: Array.from(features).sort(), options };
}

export function perfColumns(features: string[], mode: PerfMode, from: string): ColumnDef<PerfOption, unknown>[] {
  return [
    {
      id: "option",
      header: "Option",
      cell: ({ row }) => (
        <span className="flex flex-wrap items-center gap-1">
          <OptionLink optionId={row.original.option_id} itemId={row.original.item_id} from={from}>
            {row.original.option_name}
          </OptionLink>
          {row.original.lowEvidence ? <FlagChip kind="low_evidence" /> : null}
        </span>
      ),
    },
    ...features.map(
      (feature): ColumnDef<PerfOption, unknown> => ({
        id: `feature:${feature}`,
        header: feature,
        cell: ({ row }) => {
          const score = row.original.byFeature[feature];
          if (!score) {
            return (
              <Chip tone="muted" title="Not scored for this feature">
                —
              </Chip>
            );
          }
          return <MiniBar value={score[mode]} label={feature} />;
        },
      }),
    ),
  ];
}

export function FeaturePerformanceBody({ data, mode, from }: { data: RowsData<FeaturePerfRow>; mode: PerfMode; from: string }) {
  const { features, options } = pivotFeatures(data.rows, data.truncated);
  return (
    <>
      <div className="overflow-x-auto">
        <DataTable<PerfOption>
          columns={perfColumns(features, mode, from)}
          rows={options}
          page={{ pageIndex: 0, pageSize: Math.max(1, options.length), rowCount: options.length }}
          onPageChange={() => {}}
          density="catalogue"
          caption={mode === "average" ? "Average criterion score per feature" : "Lowest criterion score per feature"}
          rowClassName={(option) => (option.dropped ? MUTED_ROW : undefined)}
        />
      </div>
      <Footnote>
        <RefLegend />
      </Footnote>
      {data.truncated ? <TruncatedNote shown={options.length} /> : null}
    </>
  );
}

function FeaturePerformancePage({ tab, areaName, mode }: PageProps & { mode: PerfMode }) {
  const search = useSearch({ strict: false }) as Record<string, unknown>;
  const word = mode === "average" ? "average" : "lowest";
  return (
    <RowsPage<FeaturePerfRow>
      tab={tab}
      areaName={areaName}
      extra={pageExtra(tab.tabKey, search)}
      head={(data) => (
        <PageHead
          title={tab.label}
          sub={
            data?.rows[0]
              ? `${data.rows[0].item_name} · ${word} criterion score per feature · grey bars are below the 7.0 standard`
              : undefined
          }
        />
      )}
    >
      {(data) => <FeaturePerformanceBody data={data} mode={mode} from={tab.tabKey} />}
    </RowsPage>
  );
}

export function FeaturePerformanceAveragePage(props: PageProps) {
  return <FeaturePerformancePage {...props} mode="average" />;
}

export function FeaturePerformanceMinimumPage(props: PageProps) {
  return <FeaturePerformancePage {...props} mode="minimum" />;
}
